import {
  DashboardPeriod,
  DashboardSummary,
  SignupPoint,
} from "../../interface/dashboard.interface";

export const PERIODS: { value: DashboardPeriod; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "12m", label: "Last 12 months" },
  { value: "all", label: "All time" },
];

/** number or undefined (never NaN), so "0" and "missing" stay different */
export const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Legacy `form_stats` (monthly sign-ups by gender) -> signups_series
const fromFormStats = (rows: any[] | undefined): SignupPoint[] | undefined => {
  if (!Array.isArray(rows) || !rows.length) return undefined;
  return [...rows]
    .sort((a, b) => Number(a.year) - Number(b.year) || Number(a.monthNumber) - Number(b.monthNumber))
    .map((r) => {
      const male = num(r.signupMales) ?? 0;
      const female = num(r.signupFemales) ?? 0;
      const other = num(r.signupOther) ?? 0;
      const month = r.monthName?.slice(0, 3) || MONTHS[(Number(r.monthNumber) || 1) - 1];
      return {
        label: `${month} ${String(r.year ?? "").slice(-2)}`.trim(),
        male,
        female,
        other,
        total: male + female + other,
      };
    });
};

/**
 * Accepts both the current API response (total_users, form_stats, ...) and the
 * new contract, so the page keeps working while the backend is upgraded.
 */
export const normalizeDashboard = (raw: any): DashboardSummary => {
  if (!raw || typeof raw !== "object") return {};
  const u = raw.users ?? {};
  const r = raw.revenue ?? {};
  const rq = raw.requests ?? {};

  return {
    generated_at: raw.generated_at,
    period: raw.period,
    users: {
      total: num(u.total ?? raw.total_users),
      active: num(u.active ?? raw.active_users),
      pending: num(u.pending ?? raw.pending_users),
      flagged: num(u.flagged ?? raw.flagged_users),
      removed: num(u.removed ?? raw.removed_users),
      new_in_period: num(u.new_in_period),
      new_prev_period: num(u.new_prev_period),
    },
    revenue: {
      ngn: {
        // legacy total_revenue is treated as NGN collected; confirm with backend
        total: num(r.ngn?.total ?? raw.total_revenue),
        period: num(r.ngn?.period),
        prev_period: num(r.ngn?.prev_period),
        transactions: num(r.ngn?.transactions),
      },
      // legacy total_revenue_usd was a conversion, so it is NOT used here
      usd: {
        total: num(r.usd?.total),
        period: num(r.usd?.period),
        prev_period: num(r.usd?.prev_period),
        transactions: num(r.usd?.transactions),
      },
    },
    subscriptions: raw.subscriptions,
    signups_series: raw.signups_series ?? fromFormStats(raw.form_stats),
    revenue_series: raw.revenue_series,
    funnel: raw.funnel,
    requests: {
      total: num(rq.total),
      by_status: rq.by_status,
      by_gender: rq.by_gender ?? raw.connection_requests_by_gender ?? undefined,
    },
    gender_split: raw.gender_split,
    users_by_country: raw.users_by_country,
    users_by_city: raw.users_by_city,
  };
};

/* ------------------------------------------------------------------ */
/* Preview data: dev only (?preview=1), clearly bannered on screen.    */
/* ------------------------------------------------------------------ */

export const PREVIEW_ALLOWED = process.env.REACT_APP_ENVIRONMENT === "dev";

export const SAMPLE_DASHBOARD: DashboardSummary = {
  generated_at: new Date().toISOString(),
  users: {
    total: 12480,
    active: 9312,
    pending: 214,
    flagged: 37,
    removed: 118,
    new_in_period: 846,
    new_prev_period: 731,
  },
  revenue: {
    ngn: { total: 41600000, period: 3420000, prev_period: 3010000, transactions: 1460 },
    usd: { total: 4280, period: 356, prev_period: 298, transactions: 22 },
  },
  subscriptions: {
    active: 1684,
    expiring_soon: 92,
    by_plan: [
      { plan: "STANDARD", count: 902, revenue_ngn: 8700000, revenue_usd: 620 },
      { plan: "BRONZE", count: 418, revenue_ngn: 7900000, revenue_usd: 840 },
      { plan: "SILVER", count: 251, revenue_ngn: 9600000, revenue_usd: 1140 },
      { plan: "GOLD", count: 113, revenue_ngn: 10400000, revenue_usd: 1680 },
    ],
  },
  signups_series: [
    ["Dec 25", 520, 470, 18], ["Jan 26", 610, 540, 22], ["Feb 26", 580, 560, 19],
    ["Mar 26", 640, 610, 25], ["Apr 26", 700, 655, 28], ["May 26", 690, 700, 31],
    ["Jun 26", 760, 720, 27], ["Jul 26", 810, 790, 33], ["Aug 26", 780, 805, 29],
    ["Sep 26", 850, 830, 35], ["Oct 26", 905, 880, 38], ["Nov 26", 420, 405, 21],
  ].map(([label, male, female, other]) => ({
    label: label as string,
    male: male as number,
    female: female as number,
    other: other as number,
    total: (male as number) + (female as number) + (other as number),
  })),
  revenue_series: [
    ["Dec 25", 1.9, 180], ["Jan 26", 2.3, 210], ["Feb 26", 2.1, 260], ["Mar 26", 2.6, 240],
    ["Apr 26", 2.8, 300], ["May 26", 2.7, 290], ["Jun 26", 3.0, 330], ["Jul 26", 3.3, 380],
    ["Aug 26", 3.2, 350], ["Sep 26", 3.6, 410], ["Oct 26", 3.9, 450], ["Nov 26", 1.6, 190],
  ].map(([label, m, usd]) => ({
    label: label as string,
    ngn: (m as number) * 1_000_000,
    usd: usd as number,
  })),
  funnel: [
    { stage: "Opt-in", count: 12480 },
    { stage: "Phone verified", count: 9870 },
    { stage: "Email verified", count: 8120 },
    { stage: "Code received", count: 6540 },
    { stage: "Paid", count: 1684 },
    { stage: "Elite member", count: 1290 },
  ],
  requests: {
    total: 5320,
    by_status: { pending: 812, accepted: 2940, rejected: 610, closed: 958 },
    by_gender: { MALE: 2870, FEMALE: 2450 },
  },
  gender_split: { MALE: 6380, FEMALE: 5870, OTHERS: 230 },
  users_by_country: [
    { country: "Nigeria", iso2: "NG", users: 7420 },
    { country: "Ghana", iso2: "GH", users: 1180 },
    { country: "United Kingdom", iso2: "GB", users: 960 },
    { country: "United States", iso2: "US", users: 830 },
    { country: "Kenya", iso2: "KE", users: 540 },
    { country: "South Africa", iso2: "ZA", users: 410 },
    { country: "Canada", iso2: "CA", users: 360 },
    { country: "Germany", iso2: "DE", users: 190 },
    { country: "United Arab Emirates", iso2: "AE", users: 160 },
    { country: "Cameroon", iso2: "CM", users: 140 },
    { country: "Côte d'Ivoire", iso2: "CI", users: 95 },
    { country: "Ireland", iso2: "IE", users: 70 },
    { country: "Rwanda", iso2: "RW", users: 65 },
    { country: "India", iso2: "IN", users: 40 },
    { country: "Brazil", iso2: "BR", users: 20 },
  ],
  users_by_city: [
    { city: "Lagos", country: "Nigeria", lat: 6.5244, lng: 3.3792, users: 4100 },
    { city: "Abuja", country: "Nigeria", lat: 9.0765, lng: 7.3986, users: 1650 },
    { city: "Accra", country: "Ghana", lat: 5.6037, lng: -0.187, users: 870 },
    { city: "London", country: "United Kingdom", lat: 51.5072, lng: -0.1276, users: 720 },
    { city: "Nairobi", country: "Kenya", lat: -1.2921, lng: 36.8219, users: 430 },
    { city: "Houston", country: "United States", lat: 29.7604, lng: -95.3698, users: 310 },
    { city: "Toronto", country: "Canada", lat: 43.6532, lng: -79.3832, users: 280 },
    { city: "Johannesburg", country: "South Africa", lat: -26.2041, lng: 28.0473, users: 260 },
  ],
};
